import { ChevronDown, LogOut } from "lucide-react";
import { InitialsAvatar } from "@/components/ui/InitialsAvatar";
import { UserMenuDropdown } from "../UserMenu/UserMenuDropdown";
import { ProfileSwitcher } from "@/components/shared";
import type { User } from "@/domain/user";
import { useRef, useCallback } from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useTranslation } from "react-i18next";
import { getSidebarTheme, isDarkVariant, type SidebarVariant } from "./sidebarTheme";
import { useHubProfileSafe } from "@/hooks/hub/useHubProfileSafe";
import { cn } from "@/utils";

interface Organization {
  uuid: string;
  name: string;
  logo_signed_url?: string | null;
}

interface UserOrganization {
  organization: Organization;
  role: string;
}

interface HubSidebarSectionProps {
  isCollapsed: boolean;
  onLogout: () => void;
  theme: ReturnType<typeof getSidebarTheme>;
}

const HubSidebarSection = ({ isCollapsed, onLogout, theme }: HubSidebarSectionProps) => {
  const { t } = useTranslation();
  const hubProfile = useHubProfileSafe();

  const hasMultipleProfiles = hubProfile?.hasMultipleProfiles ?? false;
  const selectedProfile = hubProfile?.selectedProfile;

  return (
    <div className="p-3 space-y-2">
      {/* Logout button - always visible */}
      <button
        onClick={onLogout}
        className={cn(
          "w-full flex items-center rounded-md transition-colors group",
          isCollapsed ? "justify-center p-2" : "px-3 py-2",
        )}
      >
        <LogOut
          size={16}
          className={cn(theme.textSecondary, "group-hover:text-danger transition-colors")}
        />
        {!isCollapsed && (
          <span
            className={cn(
              "ml-2 text-sm",
              theme.textSecondary,
              "group-hover:text-danger transition-colors",
            )}
          >
            {t("nav.logout", "Cerrar Sesión")}
          </span>
        )}
      </button>

      {/* Profile Switcher - only if multiple profiles */}
      {hasMultipleProfiles && selectedProfile && hubProfile && (
        <ProfileSwitcher
          selectedProfile={selectedProfile}
          profiles={hubProfile.profiles}
          onProfileSelect={hubProfile.selectProfile}
          variant="sidebar"
          isCollapsed={isCollapsed}
        />
      )}
    </div>
  );
};

interface SidebarUserSectionProps {
  isCollapsed: boolean;
  user: User | null;
  showMenu: boolean;
  onToggleMenu: () => void;
  variant?: SidebarVariant;
  organizationDetails?: Organization | null;
  currentOrganization?: { user_role?: string } | null;
  userOrganizations?: UserOrganization[] | null;
  onLogout?: () => void;
}

export const SidebarUserSection = ({
  isCollapsed,
  user,
  showMenu,
  onToggleMenu,
  variant = "default",
  organizationDetails,
  currentOrganization,
  userOrganizations,
  onLogout,
}: SidebarUserSectionProps) => {
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const theme = getSidebarTheme(variant);
  const isDark = isDarkVariant(variant);

  const closeMenu = useCallback(() => {
    if (showMenu) onToggleMenu();
  }, [showMenu, onToggleMenu]);
  useClickOutside(menuContainerRef, closeMenu, showMenu);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.style.display = "none";
    const parent = target.parentNode as HTMLDivElement;
    const initialsDiv = document.createElement("div");
    initialsDiv.className =
      "w-8 h-8 rounded-full bg-border-secondary text-text-secondary font-medium flex items-center justify-center text-sm";
    const names = (user?.name || `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "")
      .trim()
      .split(" ")
      .filter(Boolean);
    const firstName = names[0] ?? "";
    const lastName = names[names.length - 1] ?? "";
    const initials =
      names.length === 0
        ? "?"
        : names.length === 1
          ? firstName.charAt(0).toUpperCase()
          : (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    initialsDiv.textContent = initials;
    parent?.replaceChild(initialsDiv, target);
  };

  const getBorderClass = () => {
    return theme.userBorder;
  };

  const getBgClass = () => {
    if (showMenu) {
      return `${theme.userBgActive} shadow-md scale-102`;
    }
    return `${theme.userBg} ${theme.userBgHover} hover:shadow-md hover:scale-102`;
  };

  const getTextColor = () => {
    return theme.text;
  };

  const getSecondaryTextColor = () => {
    return theme.textSecondary;
  };

  if (variant === "hub") {
    return (
      <HubSidebarSection
        isCollapsed={isCollapsed}
        onLogout={onLogout || (() => {})}
        theme={theme}
      />
    );
  }

  return (
    <div className="p-3" ref={menuContainerRef}>
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleMenu();
          }}
          className={cn(
            "w-full flex items-center rounded-md transition-colors duration-200 shadow-sm border",
            getBorderClass(),
            isCollapsed ? "justify-center p-2 overflow-visible" : "justify-between p-2",
            getBgClass(),
          )}
        >
          {isCollapsed ? (
            <div className="overflow-visible">
              {user?.avatar || user?.profile_picture_signed_url ? (
                <div className="w-8 h-8 rounded-full bg-bg-secondary flex items-center justify-center overflow-hidden">
                  <img
                    src={user.avatar || user.profile_picture_signed_url}
                    alt="User"
                    className="w-full h-full object-cover"
                    onError={handleImageError}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center overflow-visible relative z-10">
                  <InitialsAvatar
                    fullName={user?.name || undefined}
                    firstName={user?.first_name || undefined}
                    lastName={user?.last_name || undefined}
                    size={16}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center min-w-0 overflow-hidden">
              <div className="flex-shrink-0">
                {user?.avatar || user?.profile_picture_signed_url ? (
                  <div className="w-8 h-8 rounded-full bg-bg-secondary flex items-center justify-center overflow-hidden">
                    <img
                      src={user.avatar || user.profile_picture_signed_url}
                      alt="User"
                      className="w-full h-full object-cover"
                      onError={handleImageError}
                    />
                  </div>
                ) : (
                  <InitialsAvatar
                    fullName={user?.name || undefined}
                    firstName={user?.first_name || undefined}
                    lastName={user?.last_name || undefined}
                    size={16}
                  />
                )}
              </div>
              <div className="ml-3 text-left min-w-0 flex-1 overflow-hidden">
                <p
                  className={cn(
                    "text-sm font-semibold overflow-hidden text-ellipsis whitespace-nowrap",
                    getTextColor(),
                  )}
                >
                  {user?.name || theme.badge || "User"}
                </p>
                <p
                  className={cn(
                    "text-xs overflow-hidden text-ellipsis whitespace-nowrap",
                    getSecondaryTextColor(),
                  )}
                >
                  {isDark
                    ? theme.badge === "Admin"
                      ? "Administrador"
                      : theme.badge === "Hub"
                        ? "Inversor"
                        : user?.email
                    : user?.email || "user@example.com"}
                </p>
              </div>
            </div>
          )}
          {!isCollapsed && (
            <ChevronDown
              size={16}
              className={cn(
                getSecondaryTextColor(),
                "transition-transform duration-200",
                showMenu ? "rotate-180" : "rotate-0",
              )}
            />
          )}
        </button>

        {/* User Menu Dropdown */}
        {showMenu && (
          <UserMenuDropdown
            isCollapsed={isCollapsed}
            organizationDetails={organizationDetails}
            currentOrganization={currentOrganization}
            userOrganizations={userOrganizations}
            onClose={onToggleMenu}
            onLogout={onLogout || (() => {})}
            variant={variant}
          />
        )}
      </div>
    </div>
  );
};
